import { getViews, updateViews } from "@/lib/api/views";
import { generateFilters } from "@/lib/api/filters";

interface FilterGenerationResult {
  filtersGenerated: boolean;
  shouldRedirectToSwipe?: boolean; // Whether to redirect to swipe page
  error?: string;
}

/**
 * Checks if user has created suggested filters and generates them if needed
 * This should be called after successful Instacart verification
 */
export async function generateFiltersOnLink(
  sessionId: string
): Promise<FilterGenerationResult> {
  try {
    const viewsResponse = await getViews(sessionId);

    if (viewsResponse && viewsResponse.views) {
      const hasCreatedSuggestedFilters =
        viewsResponse.views.has_created_suggested_filters;

      if (hasCreatedSuggestedFilters === false) {
        // Only generate if explicitly false
        const generateResponse = await generateFilters(sessionId);

        if (generateResponse.statusCode === 200) {
          // Check if batchDetails is empty in the response
          const batchDetails = generateResponse.body?.batchDetails || 
                              generateResponse.body?.batch_details;
          
          const hasEmptyBatchDetails = !batchDetails || 
                                      (Array.isArray(batchDetails) && batchDetails.length === 0) ||
                                      (typeof batchDetails === 'object' && Object.keys(batchDetails).length === 0);

          // If batchDetails is empty, mark as swiped and don't redirect
          await updateViews(sessionId, {
            has_created_suggested_filters: true,
            has_swiped_filters: hasEmptyBatchDetails, // Set to true if empty, false otherwise
          });
          
          return {
            filtersGenerated: true,
            shouldRedirectToSwipe: !hasEmptyBatchDetails, // Only redirect if batchDetails is not empty
          };
        } else {
          return {
            filtersGenerated: false,
            error: `Failed to generate filters: ${generateResponse.body}`,
          };
        }
      } else {
        // hasCreatedSuggestedFilters is true OR undefined/null
        return {
          filtersGenerated: false,
        };
      }
    }

    return {
      filtersGenerated: false,
      error: "No views data available",
    };
  } catch (error) {
    return {
      filtersGenerated: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}
